import { queryOptions, useQuery } from '@tanstack/react-query';

import { POLLING_DELAY_MS } from '@cast/constants';
import { CostReportClusterSummary } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { CostReportClusterSummaryNormalized } from './cost-report-normalized';
import { enhanceClusterSummary, toBigNumberTypes } from './cost-report';

const CLUSTER_DASHBOARD_SUMMARY_QUERY_KEY = 'cluster-dashboard-summary';

export const getClusterSummaryQueryKey = (clusterId: string) =>
  [CLUSTER_DASHBOARD_SUMMARY_QUERY_KEY, clusterId] as const;

export const getClusterSummaryQueryOptions = (
  clusterId: string,
  disabled = false
) =>
  queryOptions({
    queryKey: getClusterSummaryQueryKey(clusterId),
    queryFn: () =>
      apiClient.costReport.getClusterSummary({ clusterId }).then((resp) => {
        const nodesSummaries = resp.data.nodesSummaries;
        const summary = resp.data.summary;
        if (!summary) {
          return null;
        }
        const normalizedSummary = toBigNumberTypes<
          CostReportClusterSummary,
          CostReportClusterSummaryNormalized
        >(summary, ['clusterId']);

        const enhancedSummary = enhanceClusterSummary(normalizedSummary);
        return {
          summary: enhancedSummary,
          nodesSummaries,
        };
      }),
    enabled: !!clusterId && !disabled,
    refetchInterval: POLLING_DELAY_MS,
  });

export const useClusterSummaryQuery = (clusterId: string, disabled = false) => {
  const { data, isLoading, error, refetch } = useQuery(
    getClusterSummaryQueryOptions(clusterId, disabled)
  );

  return {
    summary: data?.summary,
    nodesSummaries: data?.nodesSummaries || undefined,
    isLoading,
    error,
    refetch,
  };
};
